import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Loader2, CheckCircle2 } from "lucide-react";
import { BRAND, NETTOYAGE_SERVICES, ESPACES_VERTS_SERVICES } from "@/lib/content";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  { group: "Nettoyage", options: NETTOYAGE_SERVICES.map((s) => ({ value: `nettoyage:${s.key}`, label: s.title })) },
  { group: "Espaces Verts", options: ESPACES_VERTS_SERVICES.map((s) => ({ value: `espaces_verts:${s.key}`, label: s.title })) },
];

export default function Contact() {
  const [form, setForm] = useState({
    nom: "",
    email: "",
    telephone: "",
    service: "",
    adresse: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.nom || !form.email || !form.telephone || !form.service) {
      toast.error("Merci de remplir les champs obligatoires.");
      return;
    }
    setLoading(true);
    try {
      const [groupKey, subKey] = form.service.split(":");
      const payload = {
        nom: form.nom,
        email: form.email,
        telephone: form.telephone,
        service: groupKey, // nettoyage | espaces_verts
        sous_service: subKey,
        message: form.message,
        adresse: form.adresse,
      };
      await axios.post(`${API}/quotes`, payload);
      setSent(true);
      toast.success("Demande envoyée ! Nous vous recontactons rapidement.");
      setForm({ nom: "", email: "", telephone: "", service: "", adresse: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || "Une erreur est survenue. Réessayez.";
      toast.error(typeof msg === "string" ? msg : "Une erreur est survenue.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-[#FDFCFB]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-5 gap-12 lg:gap-16">
        {/* Left: Info */}
        <div className="lg:col-span-2">
          <div className="text-xs uppercase tracking-widest font-semibold text-[#5C635A]">
            — Contact & devis
          </div>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-[#1E2320] leading-tight">
            Parlons de votre
            <br />
            <span className="text-[#1A4331]">projet.</span>
          </h2>
          <p className="mt-6 text-base text-[#5C635A] leading-relaxed">
            Décrivez-nous votre besoin : nous revenons vers vous sous 24h ouvrées
            avec une proposition adaptée et un devis gratuit.
          </p>

          <ul className="mt-10 space-y-5">
            <li className="flex gap-4">
              <span className="grid place-items-center h-11 w-11 rounded-xl bg-[#F3F4F1] text-[#1A4331] shrink-0">
                <Phone className="h-5 w-5" />
              </span>
              <div>
                <div className="text-xs uppercase tracking-wider text-[#5C635A] font-semibold">
                  Téléphone
                </div>
                <a
                  href={BRAND.phoneHref}
                  data-testid="contact-info-phone"
                  className="font-display text-lg text-[#1E2320] hover:text-[#1A4331]"
                >
                  {BRAND.phone}
                </a>
              </div>
            </li>
            <li className="flex gap-4">
              <span className="grid place-items-center h-11 w-11 rounded-xl bg-[#F3F4F1] text-[#1A4331] shrink-0">
                <Mail className="h-5 w-5" />
              </span>
              <div>
                <div className="text-xs uppercase tracking-wider text-[#5C635A] font-semibold">
                  Email
                </div>
                <a
                  href={BRAND.emailHref}
                  data-testid="contact-info-email"
                  className="font-display text-lg text-[#1E2320] hover:text-[#1A4331]"
                >
                  {BRAND.email}
                </a>
              </div>
            </li>
            {BRAND.address && (
              <li className="flex gap-4">
                <span className="grid place-items-center h-11 w-11 rounded-xl bg-[#F3F4F1] text-[#1A4331] shrink-0">
                  <MapPin className="h-5 w-5" />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#5C635A] font-semibold">
                    Adresse
                  </div>
                  <div data-testid="contact-info-address" className="font-display text-lg text-[#1E2320]">
                    {BRAND.address}
                  </div>
                </div>
              </li>
            )}
            <li className="flex gap-4">
              <span className="grid place-items-center h-11 w-11 rounded-xl bg-[#F3F4F1] text-[#1A4331] shrink-0">
                <Clock className="h-5 w-5" />
              </span>
              <div>
                <div className="text-xs uppercase tracking-wider text-[#5C635A] font-semibold">
                  Horaires
                </div>
                <div data-testid="contact-info-hours" className="font-display text-lg text-[#1E2320]">
                  {BRAND.hours}
                </div>
              </div>
            </li>
          </ul>
        </div>

        {/* Right: Form */}
        <div className="lg:col-span-3">
          <div className="rounded-3xl bg-white border border-[#E5E7E2] p-7 md:p-10 shadow-sm">
            {sent ? (
              <div data-testid="quote-success" className="py-12 text-center">
                <div className="mx-auto grid place-items-center h-14 w-14 rounded-full bg-[#1A4331]/10 text-[#1A4331]">
                  <CheckCircle2 className="h-7 w-7" />
                </div>
                <h3 className="mt-6 font-display text-2xl font-medium text-[#1E2320]">
                  Demande bien reçue !
                </h3>
                <p className="mt-3 text-[#5C635A] max-w-sm mx-auto">
                  Merci pour votre confiance. Nous revenons vers vous dans les plus brefs délais.
                </p>
                <button
                  type="button"
                  onClick={() => setSent(false)}
                  data-testid="quote-success-new"
                  className="mt-8 inline-flex items-center rounded-full px-6 py-2.5 text-sm font-medium bg-[#1A4331] text-white hover:bg-[#123022]"
                >
                  Envoyer une autre demande
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} data-testid="quote-form" className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="nom" className="text-xs uppercase tracking-wider text-[#5C635A] font-semibold">
                      Nom complet *
                    </Label>
                    <Input
                      id="nom"
                      data-testid="quote-input-nom"
                      value={form.nom}
                      onChange={onChange("nom")}
                      placeholder="Jean Dupont"
                      className="mt-2 rounded-xl h-12 border-[#E5E7E2] focus-visible:ring-[#1A4331]"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-xs uppercase tracking-wider text-[#5C635A] font-semibold">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      data-testid="quote-input-email"
                      type="email"
                      value={form.email}
                      onChange={onChange("email")}
                      placeholder="vous@email.com"
                      className="mt-2 rounded-xl h-12 border-[#E5E7E2] focus-visible:ring-[#1A4331]"
                      required
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="telephone" className="text-xs uppercase tracking-wider text-[#5C635A] font-semibold">
                      Téléphone *
                    </Label>
                    <Input
                      id="telephone"
                      data-testid="quote-input-telephone"
                      value={form.telephone}
                      onChange={onChange("telephone")}
                      placeholder="06 00 00 00 00"
                      className="mt-2 rounded-xl h-12 border-[#E5E7E2] focus-visible:ring-[#1A4331]"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#5C635A] font-semibold">
                      Service souhaité *
                    </Label>
                    <Select
                      value={form.service}
                      onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}
                    >
                      <SelectTrigger data-testid="quote-input-service" className="mt-2 rounded-xl h-12 border-[#E5E7E2]">
                        <SelectValue placeholder="Choisir un service" />
                      </SelectTrigger>
                      <SelectContent>
                        {SERVICE_OPTIONS.map((group) => (
                          <div key={group.group}>
                            <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-widest text-[#5C635A] font-semibold">
                              {group.group}
                            </div>
                            {group.options.map((o) => (
                              <SelectItem key={o.value} value={o.value} data-testid={`quote-option-${o.value}`}>
                                {o.label}
                              </SelectItem>
                            ))}
                          </div>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="adresse" className="text-xs uppercase tracking-wider text-[#5C635A] font-semibold">
                    Adresse d'intervention
                  </Label>
                  <Input
                    id="adresse"
                    data-testid="quote-input-adresse"
                    value={form.adresse}
                    onChange={onChange("adresse")}
                    placeholder="Ville et code postal"
                    className="mt-2 rounded-xl h-12 border-[#E5E7E2] focus-visible:ring-[#1A4331]"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-xs uppercase tracking-wider text-[#5C635A] font-semibold">
                    Votre message
                  </Label>
                  <Textarea
                    id="message"
                    data-testid="quote-input-message"
                    value={form.message}
                    onChange={onChange("message")}
                    placeholder="Décrivez votre besoin (surface, fréquence, délais souhaités…)"
                    rows={5}
                    className="mt-2 rounded-xl border-[#E5E7E2] focus-visible:ring-[#1A4331] resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="quote-submit-btn"
                  className="inline-flex items-center justify-center gap-2 rounded-full px-8 py-3.5 text-sm font-medium bg-[#1A4331] text-white hover:bg-[#123022] disabled:opacity-60 transition-all duration-300 w-full sm:w-auto"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Envoi en cours…
                    </>
                  ) : (
                    "Envoyer ma demande de devis"
                  )}
                </button>

                <p className="text-xs text-[#5C635A] pt-2">
                  En envoyant ce formulaire, vous acceptez d'être recontacté par FBSERVICES.
                  Aucune donnée n'est transmise à des tiers.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
